import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { devicesUrl, errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { useV2Account } from '../useV2Account';
import { TABS } from './DevicePage';

const list = (v) => (Array.isArray(v) ? v : []);
const labelsText = (d) => Object.entries(d.labels || {}).sort(([x], [y]) => x.localeCompare(y)).map(([k, v]) => `${k} is ${v}`);

// Devices, V2: the machines of an account. A machine opens to its own page: what the system info script gathered about it, and its software.
export default function DevicesPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const { accounts, account, choose, error: accountsError } = useV2Account();
  const [state, setState] = useState({ state: 'idle', devices: [] });
  const [filter, setFilter] = useState('');
  const code = account?.code;

  const load = useCallback(async () => {
    if (!code) return;
    setState({ state: 'loading', devices: [] });
    try {
      const response = await apiRef.current.request('GET', devicesUrl(code));
      setState({ state: 'ok', devices: list(response.data?.devices), available: response.data?.devicesAvailable });
    } catch (ex) {
      setState({ state: 'error', devices: [], error: errorText(ex) });
    }
  }, [code]);

  useEffect(() => { setFilter(''); load(); }, [load]);

  const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = state.devices.filter((d) => words.every((w) => `${d.hostname} ${labelsText(d).join(' ')}`.toLowerCase().includes(w)));

  return (
    <div>
      <div className="v2-head">
        <div>
          <h1>Devices</h1>
          <p>The machines of an account. Choose one to see what it reports about itself and the software installed on it.</p>
        </div>
        <div>
          <select className="v2-select v2-account" aria-label="Account" value={code || ''} onChange={(e) => choose(e.target.value)}>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.name ? `${a.code} - ${a.name}` : a.code}</option>)}
          </select>
        </div>
      </div>

      {accountsError && <div className="v2-alert err">{accountsError}</div>}
      {state.state === 'error' && <div className="v2-alert err">{state.error}</div>}
      {state.state === 'ok' && state.available === false && <div className="v2-alert info">The machines of this account could not be read right now.</div>}

      <section className="v2-card">
        <div className="v2-bar">
          <input className="v2-input" type="search" aria-label="Filter machines" placeholder="Filter by name or label" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 280 }} />
        </div>
        {state.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
        {state.state === 'ok' && (state.devices.length === 0 ? <div className="v2-empty">This account has no machines.</div> : (
          <table className="v2-table" aria-label="Devices">
            <thead>
              <tr>
                <th>Machine</th><th>Connection</th><th>Labels</th><th>Profiles that apply</th><th>Open</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => (
                <tr key={d.deviceId}>
                  <td style={{ fontWeight: 700 }}><Link to={`/v2/devices/${encodeURIComponent(d.deviceId)}?account=${encodeURIComponent(code)}`}>{d.hostname}</Link></td>
                  <td><span className={`v2-pill ${/^connected$/i.test(d.connectionState || '') ? 'ok' : 'none'}`}>{d.connectionState || 'Unknown'}</span></td>
                  <td>{labelsText(d).map((t) => <span className="v2-chip" key={t}>{t}</span>)}</td>
                  <td>{list(d.ruleSets).length}</td>
                  <td className="nowrap">
                    {TABS.map((t) => (
                      <Link key={t.id} to={`/v2/devices/${encodeURIComponent(d.deviceId)}?account=${encodeURIComponent(code)}&tab=${t.id}`} aria-label={`${t.label} of ${d.hostname}`} style={{ marginRight: 12 }}>{t.label}</Link>
                    ))}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={5}><div className="v2-empty">No machine matches the filter.</div></td></tr>}
            </tbody>
          </table>
        ))}
      </section>
    </div>
  );
}
